import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import SEO from '../common/SEO';
import GoogleMaps from '../components/GoogleMaps';
import ServicesNavigationFooter from '../components/ServicesNavigationFooter';
import * as FiIcons from 'react-icons/fi';

const { FiTool, FiHome, FiShield, FiLayers, FiPaintBucket, FiGrid } = FiIcons;

function Services() {
  const services = [
    {
      icon: FiLayers,
      title: 'Trocken- und Akustikbau',
      description: 'Professionelle Trockenbauarbeiten für die optimale Raumaufteilung und Akustiklösungen für besseren Schallschutz.',
      details: [
        'Trennwände und Vorsatzschalen',
        'Abgehängte Decken',
        'Schallschutz und Akustikdämmung',
        'Brandschutzwände',
        'Designelemente aus Gipskarton'
      ],
      image: 'https://mx.ausbau.nrw/img/Trocken_und_Akustikbau.jpg',
      link: '/trockenbau'
    },
    {
      icon: FiShield,
      title: 'Dämmtechnik',
      description: 'Hochwertige Dämmarbeiten für optimale Energieeffizienz und verbessertes Wohnklima.',
      details: [
        'Wärmedämmung Innenbereich',
        'Schalldämmung',
        'Dampfbremsen und Dampfsperren',
        'Dämmstoffe verschiedener Materialien',
        'Energieberatung und -optimierung'
      ],
      image: 'https://mx.ausbau.nrw/img/Daemmtechnik.jpg',
      link: '/daemmtechnik'
    },
    {
      icon: FiHome,
      title: 'Innentürmontage',
      description: 'Fachgerechte Montage von Innentüren aller Art mit präziser Passgenauigkeit und dauerhafter Funktionalität.',
      details: [
        'Zimmertüren verschiedener Materialien',
        'Schiebetüren und Falttüren',
        'Glastüren und Designtüren',
        'Zargen und Beschläge',
        'Maßanfertigungen'
      ],
      image: 'https://mx.ausbau.nrw/img/Innentuermontage.jpg',
      link: '/innentuer'
    },
    {
      icon: FiGrid,
      title: 'Bodenbelagsarbeiten',
      description: 'Verlegung verschiedener Bodenbeläge von Laminat bis Fliesen mit professioneller Vorbereitung des Untergrunds.',
      details: [
        'Laminat und Parkett',
        'Vinyl- und PVC-Böden',
        'Fliesen und Naturstein',
        'Teppichböden',
        'Untergrundvorbereitung und Estricharbeiten'
      ],
      image: 'https://mx.ausbau.nrw/img/Bodenbelagsarbeiten.jpg',
      link: '/bodenbelag'
    },
    {
      icon: FiPaintBucket,
      title: 'Maler- und Beschichtungsarbeiten',
      description: 'Professionelle Malerarbeiten und Oberflächenbeschichtungen für Wände, Decken und Holzelemente.',
      details: [
        'Innenanstriche aller Art',
        'Tapezierarbeiten',
        'Spachteltechniken',
        'Holzschutz und Lasuren',
        'Dekorative Beschichtungen'
      ],
      image: 'https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=600&h=400&fit=crop',
      link: '/maler'
    },
    {
      icon: FiTool,
      title: 'PU-Designböden (3D Optik)',
      description: 'Moderne Designböden mit beeindruckender 3D-Optik für einzigartige und individuelle Raumgestaltung.',
      details: [
        '3D-Epoxidharzböden',
        'Individuelle Motivgestaltung',
        'Hochglanz-Versiegelung',
        'Rutschfeste Oberflächen',
        'Fugenlose Verlegung'
      ],
      image: 'https://mx.ausbau.nrw/img/PU-Designboeden.jpg',
      link: '/designboden'
    },
  ];

  return (
    <div className="py-20">
      <SEO 
        title="Dienstleistungen Innenausbau & Sanierung | MK-AUSBAU" 
        description="Unser Leistungsspektrum: Trockenbau, Dämmtechnik, Bodenbeläge, Innentüren, Malerarbeiten & Designböden. Alles aus einer Hand in Heinsberg."
        canonical="/services"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Unsere Dienstleistungen
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Entdecken Sie unser umfassendes Leistungsspektrum im Innenausbau und der Sanierung. Jede Dienstleistung wird mit höchster Präzision und Qualität ausgeführt.
          </p>
        </motion.div>

        <div className="space-y-16">
          {services.map((service, index) => (
            <motion.div 
              key={service.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 lg:gap-12 items-center group`}
            >
              <div className="flex-1">
                <Link to={service.link} className="block">
                  <img 
                    src={service.image} 
                    alt={service.title} 
                    className="w-full h-80 object-cover rounded-xl shadow-lg group-hover:shadow-xl transition-shadow duration-300"
                  />
                </Link>
              </div>
              <div className="flex-1 space-y-6">
                <div className="flex items-center space-x-4">
                  <Link 
                    to={service.link}
                    className="w-12 h-12 bg-mk-teal-500 rounded-lg flex items-center justify-center hover:bg-mk-teal-600 transition-colors"
                  >
                    <SafeIcon icon={service.icon} className="w-6 h-6 text-white" />
                  </Link>
                  <Link to={service.link} className="hover:text-mk-teal-600 transition-colors">
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                      {service.title}
                    </h2>
                  </Link>
                </div>
                <p className="text-lg text-gray-600 leading-relaxed">
                  {service.description}
                </p>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Unsere Leistungen im Detail:
                  </h3>
                  <ul className="space-y-2">
                    {service.details.map((detail, detailIndex) => (
                      <li key={detailIndex} className="flex items-center space-x-3">
                        <div className="w-2 h-2 bg-mk-red-700 rounded-full"></div>
                        <span className="text-gray-700">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="pt-4">
                  <Link 
                    to={service.link}
                    className="inline-flex items-center space-x-2 bg-mk-teal-500 hover:bg-mk-teal-600 text-white px-6 py-3 rounded-sm font-semibold transition-colors"
                  >
                    <span>Mehr erfahren</span>
                    <SafeIcon icon={FiTool} className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
          className="text-center mt-16 bg-mk-teal-50 p-8 rounded-xl"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Interessiert an unseren Dienstleistungen?
          </h2>
          <p className="text-gray-600 mb-6">
            Kontaktieren Sie uns für ein unverbindliches Beratungsgespräch und einen kostenlosen Kostenvoranschlag.
          </p>
          <Link 
            to="/kontakt"
            className="inline-block bg-mk-red-700 hover:bg-mk-red-800 text-white px-8 py-3 rounded-sm font-semibold transition-colors"
          >
            Jetzt Kontakt aufnehmen
          </Link>
        </motion.div>
      </div>
      
      <ServicesNavigationFooter />
      <GoogleMaps />
    </div>
  );
}

export default Services;